import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import './SectionNav.css'

/**
 * In-page sub-navigation for a screen with more sections than a tab strip can
 * carry. Settings is 14 sections over two pages.
 *
 * A horizontal strip above the panel it switches. Icon-led, because on
 * `/settings/advanced` all seven entries open with the same word (`إعدادات …`),
 * so without a glyph per section the first glance is seven identical labels.
 *
 * Sections are grouped, and a group may be unlabelled — the fading hairline
 * beside it is the separation, so no new wording is invented for a divider.
 *
 * An item with `to` keeps its own route, so it stays a real link and carries a
 * chevron: "this leaves for another page" and "this swaps the panel below me"
 * should not look identical.
 *
 * @param {{title?: string, items: {id: string, label: string, to?: string,
 *          icon?: import('react').ElementType}[]}[]} groups
 */
export default function SectionNav({ groups, value, onChange, ariaLabel }) {
  return (
    <nav className="section-nav" aria-label={ariaLabel}>
      {groups.map((group, index) => (
        <div key={group.title ?? index} className="section-nav__group">
          {group.title && (
            <p className="section-nav__group-title">
              <bdi>{group.title}</bdi>
            </p>
          )}
          {group.items.map((item) => {
            const Icon = item.icon
            const selected = item.id === value
            const className = `section-nav__item${selected ? ' is-selected' : ''}`
            const content = (
              <>
                <span className="section-nav__icon" aria-hidden="true">
                  {Icon ? <Icon size={16} strokeWidth={1.9} /> : null}
                </span>
                <bdi className="section-nav__label">{item.label}</bdi>
                {/* The chevron points "onward" — left in Arabic, right in
                    English. SectionNav.css turns it. */}
                {item.to && (
                  <ChevronLeft
                    className="section-nav__go"
                    size={14}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                )}
              </>
            )

            return item.to ? (
              <Link
                key={item.id}
                to={item.to}
                className={className}
                data-testid="tab"
                data-tab-id={item.id}
              >
                {content}
              </Link>
            ) : (
              <button
                key={item.id}
                type="button"
                className={className}
                aria-current={selected ? 'true' : undefined}
                onClick={() => onChange(item.id)}
                data-testid="tab"
                data-tab-id={item.id}
              >
                {content}
              </button>
            )
          })}
        </div>
      ))}
    </nav>
  )
}
